import {
  Button,
  Icons,
  notify,
  PreviewSnippet,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { ClmInstanceSelection } from 'features/cluster/autoscaler/components/ClmCompatibleFamiliesPicker';

import { buildKarpenterInstanceFamilySnippet } from '../_utils';

const SelectionPlaceholder = ({
  onOpenCompatibleInstancesDrawer,
}: Pick<Props, 'onOpenCompatibleInstancesDrawer'>) => {
  return (
    <Box
      data-testid="clm-selection-placeholder"
      sx={{
        bgcolor: tokens.theme.bg.status.infoSubtle,
        border: tokens.theme.border.status.infoSubtle,
        borderRadius: '4px',
        px: 16,
        py: 12,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={16}
      >
        <Stack>
          <Typography variant="A2">Compatible instances</Typography>
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
          >
            Pick a base AWS family to see every compatible family in the same
            CPU generation set.
          </Typography>
        </Stack>
        <Button
          size="sm"
          endIcon={<Icons.ArrowRight />}
          onClick={() => onOpenCompatibleInstancesDrawer()}
          testId="clm-find-compatible-instances-button"
        >
          Find compatible instances
        </Button>
      </Stack>
    </Box>
  );
};

type SelectionSnippetProps = {
  clmInstanceSelection: ClmInstanceSelection;
  onEditSelection: () => void;
};

const SelectionSnippet = ({
  clmInstanceSelection,
  onEditSelection,
}: SelectionSnippetProps) => {
  const totalSelectedCount =
    // adding one to account for the base instance
    1 + clmInstanceSelection.compatibleInstances.length;

  const snippet = buildKarpenterInstanceFamilySnippet({
    baseInstance: clmInstanceSelection.baseInstance,
    compatibleInstances: clmInstanceSelection.compatibleInstances,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      notify.success('Copied to clipboard');
    } catch {
      notify.error('Failed to copy snippet');
    }
  };

  return (
    <Stack data-testid="clm-selection-snippet">
      <Box
        sx={{
          bgcolor: tokens.theme.bg.status.infoSubtle,
          border: '1px solid',
          borderBottom: 'none',
          borderColor: tokens.theme.border.status.infoSubtle,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={16}
          px={16}
          py={12}
        >
          <Typography variant="A2">
            <Typography
              component="span"
              variant="inherit"
              color={tokens.theme.fg.action.primary}
            >
              {totalSelectedCount}
            </Typography>{' '}
            <Typography component="span" variant="inherit">
              compatible instance families selected
            </Typography>
          </Typography>
          <Stack direction="row" gap={12} alignItems="center">
            <Button
              size="sm"
              onClick={handleCopy}
              testId="clm-copy-snippet-button"
            >
              Copy snippet
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onEditSelection}
              testId="clm-edit-selection-button"
            >
              Edit
            </Button>
          </Stack>
        </Stack>
      </Box>
      <PreviewSnippet
        code={snippet}
        sx={{
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      />
    </Stack>
  );
};

type Props = {
  clmInstanceSelection?: ClmInstanceSelection;
  onOpenCompatibleInstancesDrawer: () => void;
};

export const InstanceFamiliesStep = ({
  clmInstanceSelection,
  onOpenCompatibleInstancesDrawer,
}: Props) => {
  if (clmInstanceSelection) {
    return (
      <SelectionSnippet
        clmInstanceSelection={clmInstanceSelection}
        onEditSelection={onOpenCompatibleInstancesDrawer}
      />
    );
  }
  return (
    <SelectionPlaceholder
      onOpenCompatibleInstancesDrawer={onOpenCompatibleInstancesDrawer}
    />
  );
};
